// Co-located "cleanup affordances" state + actions for the Inventory
// panel: bulk-material selection, deactivate / restore (single + bulk),
// and rename / deactivate-recipe. Extracted from
// `ManufacturingInventoryPanel.tsx` to keep the orchestrator under the
// file-size hygiene cap.
//
// Soft-delete model (dev-sync 184bc79f): "delete" in the UI flips
// `mfg_materials.is_active` to false. The row stays in the table so
// it can be restored by flipping the flag back to true. Restoration
// is first-class (separate Restore button on inactive rows + bulk
// Restore in the selection toolbar) so users on mobile don't have to
// open the Edit modal to undo a mistake. Until we add a permanent-
// purge cron + audit table, every soft-deleted row is restorable
// indefinitely. See `docs/planning/mfg_material_delete_with_restore.md`
// for the proposed retention + archive design.
//
// All callbacks return the `{ ok: true } | { ok: false; error }` shape
// the duplicate-finder rows + the bulk toolbar already consume. Bulk
// callbacks aggregate per-row results into a `{ updated, failed }`
// summary that mirrors the bulk-category-reassign return shape.
import { useCallback, useState } from "react";
import {
  updateMaterial as updateMaterialApi,
  updateManufacturingRecipe as updateRecipeApi,
} from "@/lib/manufacturingClient";
import type { MfgMaterial } from "@/lib/manufacturing/types";
import {
  applyBulkActiveFlag,
  type BulkActiveFlagSummary,
  type RowUpdaterResult,
} from "./applyBulkActiveFlag";

type AsyncResult = { ok: true } | { ok: false; error: string };
type BulkSummary = BulkActiveFlagSummary;

type Opts = {
  /** Refreshes the parent materials list after a write. */
  refreshMaterials: () => void;
  /** Refreshes the recipes summary + recipe inventory after a write. */
  refreshRecipes: () => void;
};

async function setMaterialActive(materialId: string, isActive: boolean): Promise<RowUpdaterResult> {
  const r = await updateMaterialApi({ material_id: materialId, is_active: isActive });
  if (!r.ok) return { ok: false as const, error: r.error };
  return { ok: true as const };
}

export function useManufacturingInventoryCleanup({
  refreshMaterials,
  refreshRecipes,
}: Opts) {
  const [selectedMaterialIds, setSelectedMaterialIds] = useState<Set<string>>(
    () => new Set()
  );

  const toggleMaterialSelection = useCallback((id: string) => {
    setSelectedMaterialIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleMaterialSelectAllOnPage = useCallback(
    (idsOnPage: string[], selectAll: boolean) => {
      setSelectedMaterialIds((prev) => {
        const next = new Set(prev);
        if (selectAll) for (const id of idsOnPage) next.add(id);
        else for (const id of idsOnPage) next.delete(id);
        return next;
      });
    },
    []
  );

  const clearMaterialSelection = useCallback(() => {
    setSelectedMaterialIds(new Set());
  }, []);

  const deactivateMaterial = useCallback(
    async (material: MfgMaterial): Promise<AsyncResult> => {
      const r = await setMaterialActive(material.id, false);
      if (!r.ok) return r;
      refreshMaterials();
      return { ok: true as const };
    },
    [refreshMaterials]
  );

  const restoreMaterial = useCallback(
    async (material: MfgMaterial): Promise<AsyncResult> => {
      const r = await setMaterialActive(material.id, true);
      if (!r.ok) return r;
      refreshMaterials();
      return { ok: true as const };
    },
    [refreshMaterials]
  );

  const bulkDeactivateMaterials = useCallback(
    async (materialIds: ReadonlyArray<string>): Promise<BulkSummary> => {
      const summary = await applyBulkActiveFlag(materialIds, false, setMaterialActive);
      refreshMaterials();
      return summary;
    },
    [refreshMaterials]
  );

  const bulkRestoreMaterials = useCallback(
    async (materialIds: ReadonlyArray<string>): Promise<BulkSummary> => {
      const summary = await applyBulkActiveFlag(materialIds, true, setMaterialActive);
      refreshMaterials();
      return summary;
    },
    [refreshMaterials]
  );

  const renameRecipe = useCallback(
    async (recipeId: string, nextName: string): Promise<AsyncResult> => {
      const r = await updateRecipeApi({ recipe_id: recipeId, name: nextName });
      if (!r.ok) return { ok: false as const, error: r.error };
      refreshRecipes();
      return { ok: true as const };
    },
    [refreshRecipes]
  );

  const deactivateRecipe = useCallback(
    async (recipeId: string): Promise<AsyncResult> => {
      const r = await updateRecipeApi({ recipe_id: recipeId, is_active: false });
      if (!r.ok) return { ok: false as const, error: r.error };
      refreshRecipes();
      return { ok: true as const };
    },
    [refreshRecipes]
  );

  return {
    selectedMaterialIds,
    toggleMaterialSelection,
    toggleMaterialSelectAllOnPage,
    clearMaterialSelection,
    deactivateMaterial,
    restoreMaterial,
    bulkDeactivateMaterials,
    bulkRestoreMaterials,
    renameRecipe,
    deactivateRecipe,
  };
}
